import { PathwaySettingsFormStore } from "@/admin/pathways/settings/PathwaySettingsForm"
import { AdminAddTagFormState } from "@/admin/settings/tabs/AdminAddEntityTagPopoverContent"
import EntityTag from "@/admin/settings/tabs/EntityTag"
import { EntityTagListSkeleton } from "@/admin/settings/tabs/EntityTagList"
import { filterTemporaryTags, toggleTag } from "@/admin/settings/tabs/entityTagUtils"
import {
  AdminAddEntityTagPopoverContentMutation,
  AdminAddEntityTagPopoverContentMutation$data,
} from "@/admin/settings/tabs/__generated__/AdminAddEntityTagPopoverContentMutation.graphql"
import { AdminEntityTagsListPaginationFragment$key } from "@/admin/settings/tabs/__generated__/AdminEntityTagsListPaginationFragment.graphql"
import { AdminEntityTagsListPaginationQuery } from "@/admin/settings/tabs/__generated__/AdminEntityTagsListPaginationQuery.graphql"
import { AdminEntityTagsListQuery } from "@/admin/settings/tabs/__generated__/AdminEntityTagsListQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import FormStore from "@/core/form/store/FormStore"
import { ExperienceSettingsFormStore } from "@/pricing/PricingFormFields"
import { ProductEntityTagInput } from "@/product/settings/ExperienceSettingsForm"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoSpinner, DiscoText } from "@disco-ui"
import DiscoScrolledIntoView from "@disco-ui/scrolled-into-view/DiscoScrolledIntoView"
import { useTheme } from "@material-ui/core"
import { ClassNameMap } from "@material-ui/core/styles/withStyles"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { TestIDProps } from "@utils/typeUtils"
import classNames from "classnames"
import { toJS } from "mobx"
import { observer } from "mobx-react-lite"
import { Fragment } from "react"
import { usePaginationFragment } from "react-relay"
import { graphql } from "relay-runtime"

const TAGS_PER_LOAD = 20

interface Props extends TestIDProps {
  createEntityTagForm: FormStore<
    AdminAddTagFormState,
    AdminAddEntityTagPopoverContentMutation
  >
  onCreateTag: () => Promise<
    | AdminAddEntityTagPopoverContentMutation$data["response"]["node"]
    | ProductEntityTagInput
    | undefined
  >
  onSelectTag?: (tag: ProductEntityTagInput) => void
  form?: ExperienceSettingsFormStore | PathwaySettingsFormStore
  classes?: ClassNameMap<"list">
}

function AdminEntityTagsList({
  testid = "AdminEntityTagsList",
  createEntityTagForm,
  onCreateTag,
  onSelectTag,
  form,
  classes: customClasses,
}: Props) {
  const activeOrganization = useActiveOrganization()!
  const classes = useStyles()
  const theme = useTheme()
  const isDarkmode = theme.palette.type === "dark"

  const [{ organization }] =
    Relay.useRefetchableBackgroundQuery<AdminEntityTagsListQuery>(
      graphql`
        query AdminEntityTagsListQuery(
          $id: ID!
          $first: Int!
          $after: String
          $search: String
          $ignoreIds: [ID!]
        ) {
          organization: node(id: $id) {
            ... on Organization {
              ...AdminEntityTagsListPaginationFragment
                @arguments(
                  first: $first
                  after: $after
                  search: $search
                  ignoreIds: $ignoreIds
                )
            }
          }
        }
      `,
      {
        id: activeOrganization.id,
        first: TAGS_PER_LOAD,
        search: createEntityTagForm.state.name,
        ignoreIds: filterTemporaryTags(toJS(form?.state.entityTags || [])).map(
          (tag) => tag.id
        ),
      }
    )

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    AdminEntityTagsListPaginationQuery,
    AdminEntityTagsListPaginationFragment$key
  >(
    graphql`
      fragment AdminEntityTagsListPaginationFragment on Organization
      @refetchable(queryName: "AdminEntityTagsListPaginationQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
        ignoreIds: { type: "[ID!]" }
      ) {
        entityTags(first: $first, after: $after, search: $search, ignoreIds: $ignoreIds)
          @connection(key: "AdminEntityTagsList__entityTags") {
          totalCount
          edges {
            node {
              id
              name
            }
          }
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    organization
  )

  const entityTags = Relay.connectionToArray(data?.entityTags)
  const isExactMatch = !!entityTags.find(
    (tag) => tag.name.toLowerCase() === createEntityTagForm.state.name.toLowerCase()
  )

  const selectedTagsById = form ? ArrayUtils.mapBy(form?.state.entityTags, "id") : null
  const unselectedTags = entityTags.filter((tag) => !selectedTagsById?.[tag.id])

  if (!entityTags.length && !createEntityTagForm.state.name) {
    return null
  }

  return (
    <div data-testid={testid} className={classNames(classes.list, customClasses?.list)}>
      {entityTags.length || !isExactMatch ? (
        <>
          {/* If there is no exact match, allow creating a new one */}
          {!isExactMatch && createEntityTagForm.state.name && (
            <div className={classes.newTag}>
              <EntityTag
                testid={testid}
                name={createEntityTagForm.state.name}
                onClick={handleCreateAndSelectTag}
              />

              <DiscoText fontStyle={"italic"} color={"text.disabled"} variant={"body-xs"}>
                {"(New Tag)"}
              </DiscoText>
            </div>
          )}

          {unselectedTags.map((tag) => (
            <Fragment key={tag.id}>
              <EntityTag
                testid={testid}
                name={tag.name}
                onClick={() => handleSelectTag(tag)}
              />
            </Fragment>
          ))}

          {hasNext && (
            <DiscoScrolledIntoView
              onScrolledIntoView={() => loadNext(TAGS_PER_LOAD)}
              isLoading={isLoadingNext}
              skeleton={<DiscoSpinner size={"xs"} />}
            />
          )}
        </>
      ) : (
        <DiscoText
          variant={"body-sm"}
          color={isDarkmode ? "groovy.onDark.200" : "text.secondary"}
          marginTop={2}
          align={"center"}
        >
          {"No tags found."}
        </DiscoText>
      )}
    </div>
  )

  async function handleCreateAndSelectTag() {
    const newTag = await onCreateTag()
    if (!newTag) return
    handleSelectTag(newTag)
  }

  function handleSelectTag(tag: ProductEntityTagInput) {
    if (!form) return
    toggleTag(form, tag)
    if (onSelectTag) onSelectTag(tag)
  }
}

const useStyles = makeUseStyles((theme) => ({
  list: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  newTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
}))

export default Relay.withSkeleton({
  component: observer(AdminEntityTagsList),
  skeleton: EntityTagListSkeleton,
})
